import { useEffect, useState } from "react";
import { getPricingSettings, updatePricingSettings, getPricingHistory } from "../../api/client";
import AdminNav from "../../components/AdminNav";
import { PACKAGE_TYPE_LABELS } from "../../utils/packageLabels";

const EDITABLE_FIELDS = [
  { key: "baseFare", label: "Base Fare (₦)", hint: "Flat starting cost for any delivery" },
  { key: "ratePerKm", label: "Rate Per KM (₦)", hint: "Cost per kilometer traveled" },
  { key: "commissionPercent", label: "Commission (%)", hint: "Platform cut from each delivery" },
  { key: "minimumFare", label: "Minimum Fare (₦)", hint: "Lowest total price per order" },
  { key: "surgeMultiplier", label: "Surge Multiplier", hint: "Dynamic multiplier applied when active" },
  { key: "insuranceRatePercent", label: "Insurance Rate (%)", hint: "Percentage of declared value charged for package protection" },
  { key: "minInsuranceFee", label: "Min Insurance Fee (₦)", hint: "Minimum protection fee threshold" },
  { key: "subscriptionMonthlyPrice", label: "FASTSEND Plus Monthly Price (₦)", hint: "Monthly cost for Plus tier" },
  { key: "plusDiscountPercent", label: "FASTSEND Plus Discount (%)", hint: "Flat discount applied to every delivery for Plus members" },
  { key: "saverMonthlyPrice", label: "Saver Plan Monthly Price (₦)", hint: "Monthly cost for the Saver tier" },
  { key: "saverDeliveryCap", label: "Saver Delivery Cap", hint: "Deliveries per month the Saver discount covers" },
  { key: "saverMaxDiscountPercent", label: "Saver Max Discount (%)", hint: "Ceiling discount applied while under the cap" },
  { key: "collectOnBehalfFee", label: "Collect-on-Behalf Fee (₦)", hint: "Fee once this feature goes live" },
  { key: "walletDiscountPercent", label: "Wallet Payment Discount (%)", hint: "Discount for paying from wallet balance" },
  { key: "referralBonusAmount", label: "Referral Bonus (₦)", hint: "Voucher paid when a referred dispatcher is approved" }
];

const VEHICLE_LABELS = { bike: "Bike", van: "Van", truck: "Truck" };
const RA_METHOD_LABELS = { pin: "PIN", signature: "Signature", photo: "Photo", id_photo: "ID Photo" };

export default function PricingSettings() {
  const [settings, setSettings] = useState(null);
  const [form, setForm] = useState({});
  const [history, setHistory] = useState([]);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [newCity, setNewCity] = useState("");

  const load = async () => {
    try {
      const [{ data: settingsData }, { data: historyData }] = await Promise.all([
        getPricingSettings(),
        getPricingHistory()
      ]);
      setSettings(settingsData);
      setForm(settingsData);
      setHistory(historyData);
    } catch (err) {
      setError(err.response?.data?.error || "Could not load pricing settings");
    }
  };

  useEffect(() => { load(); }, []);

  const handleChange = (key, value) => {
    setSaved(false);
    setForm({ ...form, [key]: Number(value) });
  };

  const handleNestedChange = (group, key, value) => {
    setSaved(false);
    setForm({ ...form, [group]: { ...form[group], [key]: Number(value) } });
  };

  const handleSurgeToggle = () => {
    setSaved(false);
    setForm({ ...form, surgeEnabled: !form.surgeEnabled });
  };

  const handleCollectOnBehalfToggle = () => {
    setSaved(false);
    setForm({ ...form, collectOnBehalfEnabled: !form.collectOnBehalfEnabled });
  };

  const handleAddCity = () => {
    if (!newCity.trim()) return;
    const cities = form.launchedCities || [];
    if (cities.includes(newCity.trim())) return;
    setSaved(false);
    setForm({ ...form, launchedCities: [...cities, newCity.trim()] });
    setNewCity("");
  };

  const handleRemoveCity = (city) => {
    setSaved(false);
    setForm({ ...form, launchedCities: (form.launchedCities || []).filter(c => c !== city) });
  };

  const handleSave = async () => {
    setError("");
    setSaving(true);
    try {
      const { data } = await updatePricingSettings(form);
      setSettings(data);
      setForm(data);
      setSaved(true);
      const { data: historyData } = await getPricingHistory();
      setHistory(historyData);
    } catch (err) {
      setError(err.response?.data?.error || "Could not save pricing settings");
    } finally {
      setSaving(false);
    }
  };

  if (!settings) return (
    <div className="admin-app">
      <AdminNav />
      <div className="page wide page-with-nav">
        {error ? <p className="error">{error}</p> : <div className="empty-state"><p className="empty-state-text waiting-dots">Loading settings</p></div>}
      </div>
    </div>
  );

  return (
    <div className="admin-app">
      <AdminNav />
      <div className="page wide page-with-nav">

      {error && <p className="error">{error}</p>}
      {saved && <div style={{ marginBottom: "16px" }}><span className="status-badge">Saved Successfully</span></div>}

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
        {EDITABLE_FIELDS.map(({ key, label, hint }) => (
          <div key={key} className="form-field">
            <label>{label}</label>
            <input type="number" value={form[key] ?? ""} onChange={(e) => handleChange(key, e.target.value)} />
            <p className="hint">{hint}</p>
          </div>
        ))}
      </div>

      {/* Package weight-class modifiers */}
      <h3 style={{ marginTop: "24px" }}>Package Weight-Class Modifiers</h3>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "16px" }}>
        {Object.keys(PACKAGE_TYPE_LABELS).map((key) => (
          <div key={key} className="form-field">
            <label>{PACKAGE_TYPE_LABELS[key]}</label>
            <input
              type="number" step="0.1"
              value={form.packageModifiers?.[key] ?? ""}
              onChange={(e) => handleNestedChange("packageModifiers", key, e.target.value)}
            />
          </div>
        ))}
      </div>

      {/* Vehicle-type modifiers */}
      <h3 style={{ marginTop: "24px" }}>Vehicle Type Modifiers</h3>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "16px" }}>
        {Object.keys(VEHICLE_LABELS).map((key) => (
          <div key={key} className="form-field">
            <label>{VEHICLE_LABELS[key]}</label>
            <input
              type="number" step="0.1"
              value={form.vehicleModifiers?.[key] ?? ""}
              onChange={(e) => handleNestedChange("vehicleModifiers", key, e.target.value)}
            />
          </div>
        ))}
      </div>

      {/* Receiver Acknowledgement fees */}
      <h3 style={{ marginTop: "24px" }}>Receiver Acknowledgement Fees (₦)</h3>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: "16px" }}>
        {Object.keys(RA_METHOD_LABELS).map((key) => (
          <div key={key} className="form-field">
            <label>{RA_METHOD_LABELS[key]}</label>
            <input
              type="number"
              value={form.raFees?.[key] ?? ""}
              onChange={(e) => handleNestedChange("raFees", key, e.target.value)}
            />
          </div>
        ))}
      </div>

      {/* Launched Cities */}
      <h3 style={{ marginTop: "24px" }}>Launched Cities</h3>
      <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", marginBottom: "10px" }}>
        {(form.launchedCities || []).map((city) => (
          <span key={city} className="status-badge info" style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            {city}
            <button
              type="button"
              onClick={() => handleRemoveCity(city)}
              style={{ background: "none", border: "none", color: "inherit", cursor: "pointer", fontWeight: 700, padding: 0 }}
            >✕</button>
          </span>
        ))}
      </div>
      <div style={{ display: "flex", gap: "8px", maxWidth: "320px" }}>
        <input type="text" placeholder="Add a city, e.g. Ibadan" value={newCity} onChange={(e) => setNewCity(e.target.value)} style={{ marginBottom: 0 }} />
        <button type="button" className="secondary" style={{ width: "auto", padding: "8px 16px", marginTop: 0 }} onClick={handleAddCity}>+ Add</button>
      </div>

      {/* Surge Switch */}
      <div className="surge-toggle" style={{ marginTop: "24px" }}>
        <div className="surge-label">
          <strong>Surge Pricing Mode</strong>
          <span className="hint" style={{ display: "block" }}>
            {form.surgeEnabled ? "Surge multiplier is actively applied to new orders" : "Standard pricing active"}
          </span>
        </div>
        <button
          type="button"
          className={`toggle-switch ${form.surgeEnabled ? "on" : ""}`}
          onClick={handleSurgeToggle}
        />
      </div>

      {/* Collect-on-behalf toggle */}
      <div className="surge-toggle">
        <div className="surge-label">
          <strong>Collect Payment on Behalf of Sender</strong>
          <span className="hint" style={{ display: "block" }}>
            {form.collectOnBehalfEnabled ? "Live — charged on orders that opt in" : "Disabled — feature is built but gated off until gateway modalities are finalized"}
          </span>
        </div>
        <button
          type="button"
          className={`toggle-switch ${form.collectOnBehalfEnabled ? "on" : ""}`}
          onClick={handleCollectOnBehalfToggle}
        />
      </div>

      <button disabled={saving} onClick={handleSave} style={{ marginTop: "16px" }}>
        {saving ? "Saving Changes..." : "Save Pricing Configuration"}
      </button>

      <h3 style={{ marginTop: "32px" }}>Change Audit Log</h3>
      {history.length === 0 ? (
        <p className="hint">No pricing changes recorded yet.</p>
      ) : (
        history.map((entry) => (
          <div key={entry._id} className="history-row">
            <div>
              <strong>{entry.field}</strong>: <span style={{ color: "var(--danger)" }}>{JSON.stringify(entry.oldValue)}</span> → <span style={{ color: "var(--success)" }}>{JSON.stringify(entry.newValue)}</span>
            </div>
            <div className="hint">
              Changed by {entry.changedBy?.name || "Admin"} · {new Date(entry.changedAt).toLocaleString()}
            </div>
          </div>
        ))
      )}
      </div>
    </div>
  );
}
